import React, { useState, useRef } from 'react';
import { UserProfile, Conversation } from '../types';
import {
  X,
  Camera,
  Image,
  Sparkles,
  Music,
  MapPin,
  Users,
  Radio,
  Search,
  Check,
  Upload,
  MessageSquare,
  UserX,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

// --- CREATE STORY MODAL ---
interface CreateStoryModalProps {
  currentUser: UserProfile;
  onClose: () => void;
  onStoryCreated: (storyData: any) => void;
}

export const CreateStoryModal: React.FC<CreateStoryModalProps> = ({
  currentUser,
  onClose,
  onStoryCreated,
}) => {
  const [caption, setCaption] = useState('');
  const [music, setMusic] = useState('Wateen Ambient Beats');
  const [mood, setMood] = useState('calm');
  const [mediaUrl, setMediaUrl] = useState(
    'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&auto=format&fit=crop&q=80'
  );
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sampleImages = [
    'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop&q=80',
  ];

  const handleMediaFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isVideo = file.type.startsWith('video/');
    setMediaType(isVideo ? 'video' : 'image');

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) setMediaUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUploading) return;
    setIsUploading(true);
    let finalUrl = mediaUrl;

    if (mediaUrl.startsWith('data:')) {
      try {
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: `story_${mediaType === 'video' ? 'video.mp4' : 'img.jpg'}`,
            fileType: mediaType === 'video' ? 'video/mp4' : 'image/jpeg',
            dataBase64: mediaUrl,
            username: currentUser.username,
          }),
        });
        const data = await res.json();
        if (data.success) {
          finalUrl = data.url;
        } else {
          alert(data.error || 'فشل رفع الملف');
          setIsUploading(false);
          return;
        }
      } catch (err) {
        console.error(err);
        alert('حدث خطأ أثناء رفع القصة');
        setIsUploading(false);
        return;
      }
    }

    onStoryCreated({
      username: currentUser.username,
      mediaUrl: finalUrl,
      caption,
      music,
      mood,
      type: mediaType,
    });
    soundEffects.playSent();
    setIsUploading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl font-['Cairo'] select-none">
      <div className="relative w-full max-w-sm bg-[#0e1422] border border-slate-800 rounded-3xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
            <Camera className="w-4 h-4 text-emerald-400" />
            <span>نشر قصة جديدة على وتين (24 ساعة)</span>
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Media Preview */}
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-slate-700 bg-black flex items-center justify-center group">
            {mediaType === 'video' ? (
              <video src={mediaUrl} className="w-full h-full object-cover" autoPlay loop muted playsInline />
            ) : (
              <img src={mediaUrl} alt="Story preview" className="w-full h-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1 text-white text-xs font-bold backdrop-blur-sm"
            >
              <Upload className="w-4 h-4" />
              <span>تغيير الوسائط من جهازك</span>
            </button>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleMediaFile}
            className="hidden"
            accept="image/*,video/mp4,video/webm"
          />
          {/* Preset image picks */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
            {sampleImages.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => { setMediaUrl(img); setMediaType('image'); }}
                className={`w-12 h-12 rounded-xl overflow-hidden shrink-0 border-2 transition ${
                  mediaUrl === img ? 'border-emerald-400 scale-105' : 'border-transparent opacity-60'
                }`}
              >
                <img src={img} alt="Preset" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <div>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="اكتب وصفاً للقصة..."
              className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-1.5 px-3 py-1.5 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-slate-300">
              <Music className="w-3.5 h-3.5 text-emerald-400" />
              <input
                type="text"
                value={music}
                onChange={(e) => setMusic(e.target.value)}
                placeholder="اسم الموسيقى المصاحبة"
                className="w-full bg-transparent border-none text-xs text-white focus:outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isUploading}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 disabled:opacity-50 text-white font-bold text-xs shadow transition active:scale-95 flex items-center justify-center gap-1"
          >
            <Sparkles className="w-4 h-4" />
            <span>{isUploading ? 'جاري الرفع والنشر...' : 'مشاركة في قصص وتين'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

// --- CREATE MOMENT MODAL ---
interface CreateMomentModalProps {
  currentUser: UserProfile;
  onClose: () => void;
  onMomentCreated: (momentData: any) => void;
}

export const CreateMomentModal: React.FC<CreateMomentModalProps> = ({
  currentUser,
  onClose,
  onMomentCreated,
}) => {
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('الرياض، السعودية');
  const [mood, setMood] = useState('happy');
  const [imageUrl, setImageUrl] = useState(
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&auto=format&fit=crop&q=80'
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    onMomentCreated({
      username: currentUser.username,
      title: title.trim(),
      location,
      mood,
      imageUrl,
    });
    soundEffects.playSent();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl font-['Cairo'] select-none">
      <div className="relative w-full max-w-sm bg-[#0e1422] border border-slate-800 rounded-3xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span>تسجيل لحظة وتين جديدة</span>
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold text-slate-300 mb-1">
              عنوان اللحظة
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثال: غروب استثنائي في البحر..."
              className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              required
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-300 mb-1">
              الموقع الجغرافي
            </label>
            <div className="flex items-center gap-1.5 px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl">
              <MapPin className="w-3.5 h-3.5 text-emerald-400" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full bg-transparent border-none text-xs text-white focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-300 mb-1">
              رابط الصورة أو الخلفية
            </label>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-emerald-500"
            />
          </div>

          <button
            type="submit"
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow transition active:scale-95"
          >
            نشر اللحظة
          </button>
        </form>
      </div>
    </div>
  );
};

// --- CREATE GROUP, CHANNEL OR DIRECT CHAT MODAL ---
interface CreateGroupModalProps {
  currentUser: UserProfile;
  onClose: () => void;
  onGroupCreated: (groupData: any) => void;
  onStartDirectChat?: (targetUsername: string) => void;
  initialTab?: 'direct' | 'group' | 'channel';
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  currentUser,
  onClose,
  onGroupCreated,
  onStartDirectChat,
  initialTab = 'direct',
}) => {
  const [activeTab, setActiveTab] = useState<'direct' | 'group' | 'channel'>(initialTab);

  // --- Direct Chat State ---
  const [directSearchQuery, setDirectSearchQuery] = useState('');
  const [isSearchingDirect, setIsSearchingDirect] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [startingChatWith, setStartingChatWith] = useState<string | null>(null);

  // --- Group & Channel State ---
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([currentUser.username]);
  const [groupAvatar, setGroupAvatar] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Load initial suggested users and available members on mount
  React.useEffect(() => {
    let isMounted = true;
    const loadUsers = async () => {
      setLoadingMembers(true);
      try {
        const res = await fetch('/api/users/search');
        const data = await res.json();
        if (data.users && isMounted) {
          const others = data.users.filter((u: any) => u.username !== currentUser.username);
          setSuggestedUsers(others);
          setAvailableUsers(others);
        }
      } catch (e) {
        console.error('Error fetching suggested users:', e);
      } finally {
        if (isMounted) setLoadingMembers(false);
      }
    };
    loadUsers();
    return () => {
      isMounted = false;
    };
  }, [currentUser.username]);

  // Handle Search for Direct Messages with backend & Supabase profiles
  const handleDirectSearch = async (query: string) => {
    setDirectSearchQuery(query);
    const cleanQ = query.trim().replace(/^@/, '');

    if (!cleanQ) {
      setSearchResults([]);
      setHasSearched(false);
      setIsSearchingDirect(false);
      return;
    }

    setIsSearchingDirect(true);
    setHasSearched(true);

    try {
      // 1. Query Server API
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(cleanQ)}`);
      const data = await res.json();
      let usersList = Array.isArray(data.users) ? data.users : [];

      // 2. Query Supabase profiles table as backup / sync
      try {
        const { searchProfilesInSupabase } = await import('../lib/supabase');
        const sbProfiles = await searchProfilesInSupabase(cleanQ);
        if (sbProfiles && sbProfiles.length > 0) {
          sbProfiles.forEach((sbUser: any) => {
            if (
              !usersList.some(
                (u: any) => u.username?.toLowerCase() === sbUser.username?.toLowerCase()
              )
            ) {
              usersList.push({
                username: sbUser.username,
                displayName: sbUser.display_name || sbUser.displayName || sbUser.username,
                avatar:
                  sbUser.avatar_url ||
                  sbUser.avatar ||
                  `https://api.dicebear.com/7.x/bottts/svg?seed=${sbUser.username}`,
                email: sbUser.email,
                isVerified: Boolean(sbUser.is_verified || sbUser.isVerified),
                isPremium: Boolean(sbUser.is_premium || sbUser.isPremium),
                bio: sbUser.bio || 'مستخدم مسجل في وتين 🌿',
              });
            }
          });
        }
      } catch (err) {
        // Safe fallback
      }

      // Filter out current user from search result to prevent chatting with self
      const filtered = usersList.filter(
        (u: any) => u.username?.toLowerCase() !== currentUser.username.toLowerCase()
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error('Direct search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearchingDirect(false);
    }
  };

  // Start Direct Chat with user
  const handleStartChat = async (targetUsername: string) => {
    setStartingChatWith(targetUsername);
    soundEffects.playTap();
    try {
      if (onStartDirectChat) {
        onStartDirectChat(targetUsername);
      } else {
        // Direct fallback call
        await fetch('/api/conversations/direct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fromUsername: currentUser.username,
            targetUsername,
          }),
        });
      }
      soundEffects.playSent();
      onClose();
    } catch (err) {
      console.error('Error starting direct chat:', err);
      alert('حدث خطأ أثناء بدء المحادثة');
    } finally {
      setStartingChatWith(null);
    }
  };

  // Toggle member selection for group
  const handleToggleMember = (uname: string) => {
    soundEffects.playTap();
    setSelectedMembers((prev) =>
      prev.includes(uname) ? prev.filter((m) => m !== uname) : [...prev, uname]
    );
  };

  // Handle Group or Channel Submit
  const handleGroupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const finalMembers = activeTab === 'group' 
      ? Array.from(new Set([currentUser.username, ...selectedMembers])) 
      : [currentUser.username];

    onGroupCreated({
      type: activeTab,
      title: title.trim(),
      description: description.trim(),
      creatorUsername: currentUser.username,
      members: finalMembers,
      avatar: groupAvatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(title.trim())}`,
    });
    soundEffects.playSent();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-xl font-['Cairo'] select-none">
      <div className="relative w-full max-w-lg bg-[#111B21] border border-[#222E35] rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#202C33] bg-[#111B21]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              {activeTab === 'direct' && <MessageSquare className="w-4 h-4" />}
              {activeTab === 'group' && <Users className="w-4 h-4" />}
              {activeTab === 'channel' && <Radio className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white leading-tight">
                {activeTab === 'direct' && 'محادثة خاصة جديدة'}
                {activeTab === 'group' && 'إنشاء مجموعة جديدة'}
                {activeTab === 'channel' && 'إنشاء قناة بث عامة'}
              </h3>
              <p className="text-[11px] text-slate-400">
                {activeTab === 'direct' && 'تواصل مع مستخدمي وتين بشكل مباشر وفوري ومشفّر'}
                {activeTab === 'group' && 'اجمع أصدقاءك في غرفة محادثة تفاعلية مشتركة'}
                {activeTab === 'channel' && 'انشر أفكارك ورسائلك لجمهورك ومتابعيك'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-[#202C33] rounded-xl transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 1. Main Navigation Tabs (تبويبات علوية واضحة) */}
        <div className="p-3 bg-[#0B141A] border-b border-[#202C33]">
          <div className="grid grid-cols-3 gap-1.5 p-1 bg-[#111B21] border border-[#202C33] rounded-2xl">
            {/* Tab 1: محادثة خاصة (Default) */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('direct');
                soundEffects.playTap();
              }}
              className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold transition duration-150 ${
                activeTab === 'direct'
                  ? 'bg-emerald-500 text-[#0B141A] shadow-md shadow-emerald-500/20'
                  : 'text-slate-300 hover:text-white hover:bg-[#202C33]/60'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>محادثة خاصة</span>
            </button>

            {/* Tab 2: مجموعة جديدة */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('group');
                soundEffects.playTap();
              }}
              className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold transition duration-150 ${
                activeTab === 'group'
                  ? 'bg-emerald-500 text-[#0B141A] shadow-md shadow-emerald-500/20'
                  : 'text-slate-300 hover:text-white hover:bg-[#202C33]/60'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              <span>مجموعة جديدة</span>
            </button>

            {/* Tab 3: قناة بث */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('channel');
                soundEffects.playTap();
              }}
              className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold transition duration-150 ${
                activeTab === 'channel'
                  ? 'bg-teal-400 text-[#0B141A] shadow-md shadow-teal-400/20'
                  : 'text-slate-300 hover:text-white hover:bg-[#202C33]/60'
              }`}
            >
              <Radio className="w-3.5 h-3.5" />
              <span>قناة بث</span>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* ======================================================== */}
          {/* TAB 1: DIRECT MESSAGE (محادثة خاصة) */}
          {/* ======================================================== */}
          {activeTab === 'direct' && (
            <div className="space-y-4">
              {/* Search Bar */}
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5 flex items-center justify-between">
                  <span>ابحث بالبريد الإلكتروني أو اسم المستخدم</span>
                  {directSearchQuery && (
                    <span className="text-[10px] text-emerald-400 font-normal">
                      جاري البحث المباشر في قاعدة وتين...
                    </span>
                  )}
                </label>
                <div className="relative flex items-center">
                  <Search className="w-4 h-4 absolute right-3.5 text-emerald-400 pointer-events-none" />
                  <input
                    type="text"
                    value={directSearchQuery}
                    onChange={(e) => handleDirectSearch(e.target.value)}
                    placeholder="ابحث بالبريد الإلكتروني أو اسم المستخدم (مثال: @aymen أو omar@wateen.com)..."
                    className="w-full pl-9 pr-10 py-3 bg-[#202C33] border border-[#2A3942] rounded-2xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition shadow-inner"
                    autoFocus
                  />
                  {directSearchQuery && (
                    <button
                      type="button"
                      onClick={() => handleDirectSearch('')}
                      className="absolute left-3 p-1 text-slate-400 hover:text-white hover:bg-[#111B21] rounded-lg transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Loading State */}
              {isSearchingDirect && (
                <div className="py-8 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <div className="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">جاري البحث في مستخدمي وتين...</span>
                </div>
              )}

              {/* Search Results */}
              {!isSearchingDirect && hasSearched && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[11px] font-bold text-slate-400 px-1">
                    <span>نتائج البحث ({searchResults.length})</span>
                  </div>

                  {searchResults.length > 0 ? (
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
                      {searchResults.map((user) => (
                        <div
                          key={user.username}
                          className="p-3.5 bg-[#202C33] border border-[#2A3942] hover:border-emerald-500/50 rounded-2xl flex items-center justify-between gap-3 transition group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="relative shrink-0">
                              <img
                                src={user.avatar}
                                alt={user.displayName}
                                className="w-11 h-11 rounded-2xl object-cover border border-[#2A3942] group-hover:border-emerald-500/60 transition"
                              />
                              <div className="absolute -bottom-0.5 -left-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-[#111B21] rounded-full" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs font-bold text-white truncate">
                                  {user.displayName}
                                </span>
                                {user.isVerified && (
                                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold border border-emerald-500/20">
                                    موثق ✓
                                  </span>
                                )}
                                {user.isPremium && (
                                  <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-bold border border-amber-500/20 flex items-center gap-0.5">
                                    👑 VIP
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-emerald-400 font-mono">
                                @{user.username}
                              </div>
                              {user.email && (
                                <div className="text-[10px] text-slate-400 truncate">
                                  {user.email}
                                </div>
                              )}
                              {user.bio && (
                                <div className="text-[10px] text-slate-300 truncate max-w-xs mt-0.5">
                                  {user.bio}
                                </div>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            disabled={startingChatWith === user.username}
                            onClick={() => handleStartChat(user.username)}
                            className="shrink-0 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-[#0B141A] font-bold text-xs rounded-xl shadow-lg shadow-emerald-500/20 transition active:scale-95 flex items-center gap-1.5"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>
                              {startingChatWith === user.username ? 'جاري الفتح...' : 'بدء المحادثة'}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Friendly Not Found Card (المستخدم غير مسجل في وتين) */
                    <div className="py-8 px-4 bg-[#202C33]/50 border border-dashed border-[#2A3942] rounded-2xl text-center space-y-2">
                      <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mx-auto">
                        <UserX className="w-6 h-6" />
                      </div>
                      <h4 className="text-sm font-bold text-white">المستخدم غير مسجل في وتين</h4>
                      <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                        لم نعثر على أي حساب مطابق للبحث "{directSearchQuery}". تأكد من كتابة البريد الإلكتروني أو اسم المستخدم بدقة (مثال: @aymen).
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Suggested Contacts List when search is empty */}
              {!hasSearched && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between text-[11px] font-bold text-slate-400 px-1">
                    <span>المستخدمون المقترحون في وتين</span>
                    <span className="text-emerald-400 text-[10px]">جاهزون للتواصل</span>
                  </div>

                  <div className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
                    {suggestedUsers.length > 0 ? (
                      suggestedUsers.map((user) => (
                        <div
                          key={user.username}
                          className="p-3 bg-[#202C33] border border-[#2A3942] hover:border-emerald-500/40 rounded-2xl flex items-center justify-between gap-3 transition"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="relative shrink-0">
                              <img
                                src={user.avatar}
                                alt={user.displayName}
                                className="w-10 h-10 rounded-xl object-cover border border-[#2A3942]"
                              />
                              <div className="absolute -bottom-0.5 -left-0.5 w-3 h-3 bg-emerald-500 border-2 border-[#111B21] rounded-full" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-white truncate">
                                  {user.displayName}
                                </span>
                                {user.isVerified && (
                                  <span className="text-emerald-400 text-[10px] font-bold">✓</span>
                                )}
                              </div>
                              <div className="text-[10px] text-emerald-400 font-mono">
                                @{user.username}
                              </div>
                              <div className="text-[10px] text-slate-400 truncate max-w-xs">
                                {user.bio || 'متاح على منصة وتين 🌿'}
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            disabled={startingChatWith === user.username}
                            onClick={() => handleStartChat(user.username)}
                            className="shrink-0 px-3.5 py-1.5 bg-emerald-500/15 hover:bg-emerald-500 border border-emerald-500/30 text-emerald-400 hover:text-[#0B141A] font-bold text-xs rounded-xl transition active:scale-95 flex items-center gap-1"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>
                              {startingChatWith === user.username ? 'جاري الفتح...' : 'بدء المحادثة'}
                            </span>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="py-6 text-center text-slate-400 text-xs">
                        اكتب اسم مستخدم أو بريد إلكتروني في شريط البحث أعلاه لبدء المحادثة فوراً.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 2 & 3: NEW GROUP OR BROADCAST CHANNEL */}
          {/* ======================================================== */}
          {(activeTab === 'group' || activeTab === 'channel') && (
            <form onSubmit={handleGroupSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5">
                  اسم {activeTab === 'group' ? 'المجموعة' : 'قناة البث'}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    activeTab === 'group'
                      ? 'مثال: رواد التقنية، فريق العمل، أصدقاء وتين...'
                      : 'مثال: أخبار وتين، مدونة البرمجة، تحديثات هامة...'
                  }
                  className="w-full px-3.5 py-2.5 bg-[#202C33] border border-[#2A3942] rounded-2xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5">
                  الوصف أو نبذة تعريفية
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder={
                    activeTab === 'group'
                      ? 'عن ماذا تدور هذه المجموعة وقواعد النقاش...'
                      : 'وصف القناة ومحتوى الرسائل التي سيتم بثها...'
                  }
                  className="w-full px-3.5 py-2.5 bg-[#202C33] border border-[#2A3942] rounded-2xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition resize-none"
                />
              </div>

              {/* Group Member Selection */}
              {activeTab === 'group' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-300">
                    <span>إضافة أعضاء للمجموعة ({selectedMembers.length})</span>
                    <span className="text-emerald-400 text-[10px]">
                      محدد: {selectedMembers.length} أعضاء
                    </span>
                  </div>

                  <div className="space-y-1.5 max-h-48 overflow-y-auto p-1 bg-[#0B141A] border border-[#202C33] rounded-2xl">
                    {availableUsers.map((user) => {
                      const isSelected = selectedMembers.includes(user.username);
                      return (
                        <button
                          key={user.username}
                          type="button"
                          onClick={() => handleToggleMember(user.username)}
                          className={`w-full p-2 rounded-xl flex items-center justify-between transition text-right ${
                            isSelected
                              ? 'bg-emerald-500/15 border border-emerald-500/30'
                              : 'hover:bg-[#202C33] border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <img
                              src={user.avatar}
                              alt={user.displayName}
                              className="w-8 h-8 rounded-lg object-cover"
                            />
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-white truncate">
                                {user.displayName}
                              </div>
                              <div className="text-[10px] text-emerald-400 font-mono">
                                @{user.username}
                              </div>
                            </div>
                          </div>

                          <div
                            className={`w-5 h-5 rounded-lg flex items-center justify-center border transition ${
                              isSelected
                                ? 'bg-emerald-500 border-emerald-500 text-[#0B141A]'
                                : 'border-slate-600 bg-[#111B21]'
                            }`}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Channel Notice */}
              {activeTab === 'channel' && (
                <div className="p-3 bg-[#0B141A] border border-teal-500/20 rounded-2xl flex items-center gap-2.5 text-xs text-teal-300">
                  <Radio className="w-4 h-4 text-teal-400 shrink-0" />
                  <span>
                    قنوات وتين تتيح لك بث الرسائل والوسائط لعدد غير محدود من المتابعين، ويكون النشر محصوراً بالمشرفين فقط.
                  </span>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={!title.trim()}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 disabled:opacity-50 text-[#0B141A] font-bold text-xs shadow-lg shadow-emerald-500/20 transition active:scale-95 flex items-center justify-center gap-1.5"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>
                    {activeTab === 'group' ? 'تأكيد إنشاء المجموعة' : 'تأكيد إنشاء القناة'}
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// --- GLOBAL SEARCH MODAL ---
interface GlobalSearchModalProps {
  onClose: () => void;
  onSelectUser: (username: string) => void;
  onSelectConversation: (convId: string) => void;
  conversations: Conversation[];
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({
  onClose,
  onSelectUser,
  onSelectConversation,
  conversations,
}) => {
  const [query, setQuery] = useState('');
  const [matchedUsers, setMatchedUsers] = useState<any[]>([]);

  const handleSearch = async (val: string) => {
    setQuery(val);
    if (!val.trim()) {
      setMatchedUsers([]);
      return;
    }
    try {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(val)}`);
      const data = await res.json();
      if (data.users) setMatchedUsers(data.users);
    } catch {
      // ignore
    }
  };

  const matchedConvs = conversations.filter(
    (c) =>
      query.trim() &&
      (c.title.toLowerCase().includes(query.toLowerCase()) ||
        c.description?.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 bg-black/85 backdrop-blur-xl font-['Cairo'] select-none">
      <div className="relative w-full max-w-lg bg-[#0e1422] border border-slate-800 rounded-3xl p-5 shadow-2xl">
        <div className="relative flex items-center mb-4">
          <Search className="w-5 h-5 absolute right-3.5 text-emerald-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="ابحث عن أشخاص (@username)، مجموعات، قنوات في وتين..."
            className="w-full pl-9 pr-11 py-3 bg-[#141b27] border border-slate-800 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            autoFocus
          />
          <button onClick={onClose} className="absolute left-3 p-1 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto divide-y divide-slate-800/60">
          {/* User Results */}
          {matchedUsers.length > 0 && (
            <div className="pt-2">
              <span className="text-[10px] font-bold text-emerald-400 block mb-2">الأشخاص والهويات</span>
              <div className="space-y-1">
                {matchedUsers.map((u) => (
                  <button
                    key={u.username}
                    onClick={() => {
                      onSelectUser(u.username);
                      onClose();
                    }}
                    className="w-full p-2 rounded-xl hover:bg-[#141b27] flex items-center justify-between text-right"
                  >
                    <div className="flex items-center gap-2.5">
                      <img src={u.avatar} alt={u.displayName} className="w-9 h-9 rounded-xl object-cover" />
                      <div>
                        <div className="text-xs font-bold text-white">{u.displayName}</div>
                        <div className="text-[10px] text-emerald-400 font-mono">@{u.username}</div>
                      </div>
                    </div>
                    {u.isVerified && (
                      <span className="text-[10px] text-emerald-400 font-bold">موثق ✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation Results */}
          {matchedConvs.length > 0 && (
            <div className="pt-2">
              <span className="text-[10px] font-bold text-teal-400 block mb-2">المجموعات والقنوات</span>
              <div className="space-y-1">
                {matchedConvs.map((c, idx) => (
                  <button
                    key={`${c.id}_${idx}`}
                    onClick={() => {
                      onSelectConversation(c.id);
                      onClose();
                    }}
                    className="w-full p-2 rounded-xl hover:bg-[#141b27] flex items-center gap-2.5 text-right"
                  >
                    <img src={c.avatar} alt={c.title} className="w-9 h-9 rounded-xl object-cover" />
                    <div>
                      <div className="text-xs font-bold text-white">{c.title}</div>
                      <div className="text-[10px] text-slate-400 truncate max-w-xs">{c.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!query && (
            <div className="py-8 text-center text-slate-500 text-xs">
              اكتب اسم مستخدم أو قناة للبحث فورياً داخل شبكة وتين
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
